"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Form,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui";

import { MeasuringUnitDto, measuringUnitSchema } from "@/lib/schema/unit";
import { cn } from "@/lib/utils";
import { useGetMeasuringUnit } from "@/hooks/org/measuring-unit";
import { FormFieldWrapper } from "./form";

interface MeasuringUnitSelectProps {
  value: string;
  onChange: (value: string) => void;
}

export function MeasuringUnitSelect({
  value,
  onChange,
}: MeasuringUnitSelectProps) {
  const [open, setOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { units, createMeasuringUnit } = useGetMeasuringUnit();

  const form = useForm<MeasuringUnitDto>({
    resolver: zodResolver(measuringUnitSchema),
    defaultValues: {
      name: "",
      abbreviation: "",
    },
  });

  const selectedUnit = units.find(
    ({ name }) => name.toLowerCase() === value.toLowerCase()
  );

  return (
    <div className="flex gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedUnit
              ? `${selectedUnit.name} ${
                  selectedUnit.abbreviation
                    ? `(${selectedUnit.abbreviation})`
                    : ""
                }`
              : "Select unit..."}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0">
          <Command>
            <CommandInput placeholder="Search measuring unit..." />
            <CommandList>
              <CommandEmpty>
                No measuring unit found.
                <Button
                  variant="ghost"
                  className="ml-2"
                  onClick={() => setCreateDialogOpen(true)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create New
                </Button>
              </CommandEmpty>
              <CommandGroup>
                {units.map((unit) => (
                  <CommandItem
                    key={unit.id}
                    onSelect={() => {
                      onChange(unit.name);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === unit.name ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {unit.name}
                    {unit.abbreviation && (
                      <span className="ml-2 text-muted-foreground">
                        ({unit.abbreviation})
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Measuring Unit</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form id="create-measuring-unit-form" className="space-y-4">
              <FormFieldWrapper
                control={form.control}
                label="Name"
                name="name"
                type="text"
              />
              <FormFieldWrapper
                control={form.control}
                label="Abbreviation"
                name="abbreviation"
                type="text"
              />

              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCreateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => createMeasuringUnit(form.getValues())}
                >
                  Create
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
